import { useCustomMutation, useNotification } from '@refinedev/core';
import { Lock, Unlock, KeyRound, RefreshCw } from 'lucide-react';
import type { FC } from 'react';

import type { ByokStatus } from '@/pages/ops/ByokManagerPage';

interface Props {
  status: ByokStatus | undefined;
  orgId: string;
  onEnableClick: () => void;
  onDisableSuccess: () => void;
}

const ByokStatusCard: FC<Props> = ({ status, orgId, onEnableClick, onDisableSuccess }) => {
  const { open: notify } = useNotification();
  const { mutate: disableByok, isLoading: disabling } = useCustomMutation();

  const handleDisable = () => {
    disableByok(
      { url: `/api/v1/admin/subscription/organizations/${orgId}/byok/disable`, method: 'post', values: {} },
      {
        onSuccess: () => {
          notify?.({ type: 'success', message: 'BYOK désactivé avec succès.' });
          onDisableSuccess();
        },
        onError: () => notify?.({ type: 'error', message: 'Erreur lors de la désactivation BYOK.' }),
      },
    );
  };

  if (!status?.enabled) {
    return (
      <div className="fc-card p-6">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-700">
            <Lock className="h-6 w-6 text-slate-500 dark:text-slate-400" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-slate-900 dark:text-white">Mode Standard</h3>
              <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                Clés Plateforme
              </span>
            </div>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              L'organisation utilise les clés API de la plateforme. Consommation décomptée du quota mensuel.
            </p>
            <button onClick={onEnableClick} className="mt-4 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700">
              <Unlock className="h-4 w-4" /> Activer BYOK
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fc-card border-emerald-200 p-6 dark:border-emerald-800">
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50 dark:bg-emerald-900/30">
          <KeyRound className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-slate-900 dark:text-white">Mode BYOK Actif</h3>
            <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
              Clé Propre
            </span>
          </div>
          <div className="mt-2 space-y-1 text-sm text-slate-600 dark:text-slate-300">
            <p>Provider : <span className="font-medium capitalize">{status.provider}</span></p>
            <p>Clé : <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs dark:bg-slate-700">{status.maskedKey}</code></p>
            {status.enabledAt && <p>Activé le : {new Date(status.enabledAt).toLocaleDateString('fr-FR')}</p>}
          </div>
          <p className="mt-2 text-xs text-emerald-600 dark:text-emerald-400">Consommation illimitée — pas de quota plateforme</p>

          <div className="mt-4 flex gap-2">
            <button onClick={onEnableClick} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700">
              <RefreshCw className="h-3.5 w-3.5" /> Changer la clé
            </button>
            <button onClick={handleDisable} disabled={disabling} className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-sm font-medium text-red-600 transition hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20">
              <Lock className="h-3.5 w-3.5" /> {disabling ? 'Désactivation...' : 'Désactiver BYOK'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ByokStatusCard;
